// ============================================================================
// ADR-0024 N-3 — Related Party Register editor (Corporate Facts)
// ============================================================================
// One register per engagement (Corporate Facts `related_parties` +
// `related_party_transactions`) feeding the AS 18 disclosure in the Additional
// Information note, Form AOC-2 (rows with "Form AOC-2") and CARO (xiii).
//
// List-based: two tabs — the Register and the Transactions — each a compact
// list (search, filters, row actions) with a separate Add / Edit form. Nothing
// is typed into the list itself, so long registers stay readable. A form's
// Save puts the item into the Corporate Facts draft; the page's Save stores it.
//
// Parties are people (KMP, relatives) or entities; an entity links to the
// people through whom it is related, and its "Particulars" are generated from
// those links — so a renamed KMP renames every reference, and a party that is
// still referenced cannot be deleted.

import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import { generateLocalId } from 'components/modals/modules/fsa/notes/panes/policy/SectionedNarrativeEditor';
import { formatStatementAmount } from 'components/modals/modules/fsa/reports/reportSections';
import type {
  CorporateFactsData,
  CorporateFactsDirector,
  RelatedParty,
  RelatedPartyCategory,
  RelatedPartyLinkRole,
  RelatedPartyTransaction,
} from 'api/types/fsa/corporateFacts.types';
import {
  CATEGORY_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  LINK_ROLE_OPTIONS,
  NATURE_SUGGESTIONS,
  defaultInAoc2,
  describeParty,
  isPersonCategory,
  referencedPartyIds,
} from './relatedPartyRegister';
import { FactsField, FactsFormShell, factsFormGridStyle } from './FactsForm';

interface Props {
  data: CorporateFactsData;
  onChange: (patch: Partial<CorporateFactsData>) => void;
  /** The engagement's own directors list (Corporate Facts), in its order — offered as KMP. */
  directors?: CorporateFactsDirector[];
  /** Extra buttons on the transactions toolbar (e.g. import from the old note). */
  transactionsToolbar?: React.ReactNode;
  /** Shown above the transactions list (e.g. an import preview). */
  transactionsBanner?: React.ReactNode;
}

type Tab = 'register' | 'transactions';
type AocFilter = 'all' | 'aoc2' | 'as18';

const amount = (v: number | null | undefined) => (v == null ? '—' : formatStatementAmount(v));
const categoryLabel = (c: RelatedPartyCategory) => CATEGORY_OPTIONS.find(o => o.value === c)?.label ?? c;
const shortCategory = (c: RelatedPartyCategory) =>
  c === 'KMP' ? 'KMP' : c === 'RELATIVE_OF_KMP' ? 'Relative of KMP' : c === 'ENTITY_KMP' ? 'Enterprise of KMP' : categoryLabel(c);

const RelatedPartyRegisterEditor: React.FC<Props> = ({ data, onChange, directors, transactionsToolbar, transactionsBanner }) => {
  const parties = useMemo(() => data.related_parties ?? [], [data.related_parties]);
  const rpts = useMemo(() => data.related_party_transactions ?? [], [data.related_party_transactions]);
  const byId = useMemo(() => new Map(parties.map(p => [p.local_id, p])), [parties]);
  const referenced = useMemo(() => referencedPartyIds(parties, rpts), [parties, rpts]);
  const txnCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rpts) if (r.party_id) m.set(r.party_id, (m.get(r.party_id) ?? 0) + 1);
    return m;
  }, [rpts]);

  const [tab, setTab] = useState<Tab>('register');
  const [search, setSearch] = useState('');
  const [aocFilter, setAocFilter] = useState<AocFilter>('all');
  const [editingParty, setEditingParty] = useState<RelatedParty | null>(null);
  const [editingTxn, setEditingTxn] = useState<{ txn: RelatedPartyTransaction; isNew: boolean } | null>(null);

  // ───── parties ─────────────────────────────────────────────────────────
  const setParties = (next: RelatedParty[]) => onChange({ related_parties: next });
  const saveParty = (p: RelatedParty) => {
    const exists = parties.some(x => x.local_id === p.local_id);
    setParties(exists ? parties.map(x => (x.local_id === p.local_id ? p : x)) : [...parties, p]);
    // A renamed party renames the transactions' copy of the name.
    if (exists && rpts.some(r => r.party_id === p.local_id && r.party !== p.name)) {
      onChange({
        related_parties: parties.map(x => (x.local_id === p.local_id ? p : x)),
        related_party_transactions: rpts.map(r => (r.party_id === p.local_id ? { ...r, party: p.name } : r)),
      });
    }
    setEditingParty(null);
  };
  const newParty = (category: RelatedPartyCategory): RelatedParty => ({
    local_id: generateLocalId(), name: '', category, ...(isPersonCategory(category) ? {} : { entity_type: 'COMPANY' as const }),
  });
  const deleteParty = (id: string) => setParties(parties.filter(p => p.local_id !== id));

  const addDirectorsAsKmp = () => {
    const have = new Set(parties.map(p => p.director_id).filter(Boolean));
    const added: RelatedParty[] = (directors ?? [])
      .filter(d => !have.has(d.director_id))
      .map(d => ({ local_id: generateLocalId(), name: d.name, category: 'KMP' as const, designation: d.designation, director_id: d.director_id }));
    if (added.length > 0) setParties([...parties, ...added]);
  };

  // Transactions entered before the register existed (explicit action).
  const unlinkedNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const r of rpts) if (!r.party_id && r.party.trim()) names.set(r.party.trim().toLowerCase(), r.party.trim());
    return names;
  }, [rpts]);
  const buildFromTransactions = () => {
    const byName = new Map(parties.map(p => [p.name.trim().toLowerCase(), p.local_id]));
    const added: RelatedParty[] = [];
    for (const [key, name] of unlinkedNames) {
      if (byName.has(key)) continue;
      const id = generateLocalId();
      byName.set(key, id);
      added.push({ local_id: id, name, category: 'OTHER', entity_type: 'OTHER' });
    }
    onChange({
      related_parties: [...parties, ...added],
      related_party_transactions: rpts.map(r => (!r.party_id && r.party.trim()
        ? { ...r, party_id: byName.get(r.party.trim().toLowerCase()) ?? null }
        : r)),
    });
  };

  // ───── transactions ────────────────────────────────────────────────────
  const setRpts = (next: RelatedPartyTransaction[]) => onChange({ related_party_transactions: next });
  const saveTxn = (t: RelatedPartyTransaction) => {
    setRpts(rpts.some(x => x.local_id === t.local_id) ? rpts.map(x => (x.local_id === t.local_id ? t : x)) : [...rpts, t]);
    setEditingTxn(null);
  };
  const deleteTxn = (id: string) => setRpts(rpts.filter(r => r.local_id !== id));
  const duplicateTxn = (t: RelatedPartyTransaction) =>
    setEditingTxn({ txn: { ...t, local_id: generateLocalId(), value_cy: null, balance_cy: null }, isNew: true });

  // ───── filtering ───────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const partyMatches = (p: RelatedParty) => !q
    || p.name.toLowerCase().includes(q)
    || describeParty(p, byId).toLowerCase().includes(q)
    || shortCategory(p.category).toLowerCase().includes(q);
  const grouped = CATEGORY_OPTIONS
    .map(c => ({ ...c, items: parties.filter(p => p.category === c.value && partyMatches(p)) }))
    .filter(g => g.items.length > 0);
  const partyName = (r: RelatedPartyTransaction) => (r.party_id ? byId.get(r.party_id)?.name : undefined) ?? r.party;
  const shownTxns = rpts.filter(r => {
    const inAoc2 = r.in_aoc2 !== false;
    if (aocFilter === 'aoc2' && !inAoc2) return false;
    if (aocFilter === 'as18' && inAoc2) return false;
    if (!q) return true;
    return (partyName(r) ?? '').toLowerCase().includes(q) || (r.nature ?? '').toLowerCase().includes(q);
  });
  const txnTotal = shownTxns.reduce((s, r) => s + (r.value_cy ?? 0), 0);

  return (
    <div style={cardStyle}>
      {/* ───── tabs + toolbar ───── */}
      <div style={toolbarStyle}>
        <div style={segmentStyle} role="tablist" aria-label="Related parties">
          <button type="button" role="tab" aria-selected={tab === 'register'} style={segBtn(tab === 'register')} onClick={() => setTab('register')}>
            Register <span style={countPillStyle}>{parties.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'transactions'} style={segBtn(tab === 'transactions')} onClick={() => setTab('transactions')}>
            Transactions <span style={countPillStyle}>{rpts.length}</span>
          </button>
        </div>
        <input
          className="form-input"
          style={{ maxWidth: 240, fontSize: '0.8rem' }}
          placeholder={tab === 'register' ? 'Search name, category, particulars…' : 'Search party or nature…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search"
        />
        {tab === 'transactions' && (
          <select className="form-input" style={{ maxWidth: 170, fontSize: '0.8rem' }} value={aocFilter}
            onChange={e => setAocFilter(e.target.value as AocFilter)} aria-label="Filter">
            <option value="all">All transactions</option>
            <option value="aoc2">Form AOC-2 only</option>
            <option value="as18">AS 18 only</option>
          </select>
        )}
        <span style={{ flex: 1 }} />
        {tab === 'register' ? (
          <>
            {directors && directors.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={addDirectorsAsKmp}
                title="Add every director on the engagement's list as Key Management Personnel">
                Add directors as KMP
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => setEditingParty(newParty('KMP'))}>
              <IconSystem.add size={13} /> Person
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setEditingParty(newParty('ENTITY_KMP'))}>
              <IconSystem.add size={13} /> Entity
            </button>
          </>
        ) : (
          <>
            {transactionsToolbar}
            <button className="btn btn-primary btn-sm"
              onClick={() => setEditingTxn({ txn: { local_id: generateLocalId(), party: '', arms_length: true }, isNew: true })}>
              <IconSystem.add size={13} /> Add transaction
            </button>
          </>
        )}
      </div>

      {/* ───── Register ───── */}
      {tab === 'register' && (
        <>
          <div style={subtleStyle}>
            AS 18 register. Link each entity to the KMP / relatives through whom it is related — the note's “Particulars”
            are written from those links. Carried to next year.
          </div>
          {parties.length === 0 && unlinkedNames.size > 0 && (
            <div style={importBoxStyle}>
              {unlinkedNames.size} part{unlinkedNames.size === 1 ? 'y is' : 'ies are'} named in the transactions but not in the register.
              <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }} onClick={buildFromTransactions}>
                Build the register from the transactions
              </button>
            </div>
          )}
          {parties.length > 0 && unlinkedNames.size > 0 && (
            <div style={warnStyle}>
              {unlinkedNames.size} transaction part{unlinkedNames.size === 1 ? 'y is' : 'ies are'} not linked to the register —{' '}
              <button type="button" style={linkBtnStyle} onClick={buildFromTransactions}>add and link them</button>
            </div>
          )}
          {parties.length === 0 && unlinkedNames.size === 0 && <div style={emptyRowStyle}>No related parties recorded. Add a person or an entity.</div>}
          {parties.length > 0 && grouped.length === 0 && <div style={emptyRowStyle}>No party matches “{search}”.</div>}
          {grouped.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Details</th>
                  <th style={thStyle}>Particulars (as printed)</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Txns</th>
                  <th style={{ ...thStyle, width: 44 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {grouped.map(g => (
                  <React.Fragment key={g.value}>
                    <tr><td colSpan={5} style={groupRowStyle}>{g.label} · {g.items.length}</td></tr>
                    {g.items.map(p => {
                      const details = p.category === 'KMP' ? (p.designation || '—')
                        : p.category === 'RELATIVE_OF_KMP' ? `${p.relation || 'Relative'}${p.relative_of ? ` of ${byId.get(p.relative_of)?.name ?? '?'}` : ''}`
                          : `${ENTITY_TYPE_OPTIONS.find(o => o.value === p.entity_type)?.label ?? 'Entity'}${(p.links ?? []).length ? ` · ${(p.links ?? []).length} link${(p.links ?? []).length === 1 ? '' : 's'}` : ''}`;
                      const particulars = describeParty(p, byId);
                      const locked = referenced.has(p.local_id);
                      const actions: ActionMenuItem[] = [
                        { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => setEditingParty(p) },
                        {
                          id: 'delete', label: locked ? 'Delete (in use)' : 'Delete', icon: <IconSystem.trash size={14} />, variant: 'danger',
                          disabled: locked, onClick: () => deleteParty(p.local_id),
                        },
                      ];
                      return (
                        <tr key={p.local_id} style={rowStyle} onClick={() => setEditingParty(p)}>
                          <td style={tdStyle}><strong>{p.name || <em style={{ color: '#b45309' }}>(no name)</em>}</strong></td>
                          <td style={tdMutedStyle}>{details}</td>
                          <td style={tdStyle}>{particulars || <em style={{ color: 'var(--text-secondary)' }}>— none —</em>}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{txnCount.get(p.local_id) ?? 0}</td>
                          <td style={tdStyle} onClick={e => e.stopPropagation()}>
                            <ActionMenu id={`rp-${p.local_id}`} items={actions} />
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ───── Transactions ───── */}
      {tab === 'transactions' && (
        <>
          <div style={subtleStyle}>
            Every transaction prints in the AS 18 disclosure (grouped by nature). Transactions marked “Form AOC-2” are s.188(1)
            contracts and also print in Annexure B of the Board's Report.
          </div>
          {transactionsBanner}
          {rpts.length === 0 && <div style={emptyRowStyle}>No related-party transactions recorded.</div>}
          {rpts.length > 0 && shownTxns.length === 0 && <div style={emptyRowStyle}>No transaction matches.</div>}
          {shownTxns.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Party</th>
                  <th style={thStyle}>Nature</th>
                  <th style={thNumStyle}>Current year</th>
                  <th style={thNumStyle}>Previous year</th>
                  <th style={thNumStyle}>Balance at year end</th>
                  <th style={thStyle}>Form AOC-2</th>
                  <th style={{ ...thStyle, width: 44 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {shownTxns.map(r => {
                  const inAoc2 = r.in_aoc2 !== false;
                  const warn = !r.party_id && parties.length > 0 ? 'Not linked to the register'
                    : r.value_cy == null && r.value_py != null ? "This year's value is not entered" : null;
                  const actions: ActionMenuItem[] = [
                    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => setEditingTxn({ txn: r, isNew: false }) },
                    { id: 'dup', label: 'Duplicate', icon: <IconSystem.add size={14} />, onClick: () => duplicateTxn(r) },
                    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={14} />, variant: 'danger', onClick: () => deleteTxn(r.local_id) },
                  ];
                  return (
                    <tr key={r.local_id} style={rowStyle} onClick={() => setEditingTxn({ txn: r, isNew: false })}>
                      <td style={tdStyle}>
                        <strong>{partyName(r) || <em style={{ color: '#b45309' }}>(no party)</em>}</strong>
                        {warn && <span style={warnDotStyle} title={warn}>!</span>}
                      </td>
                      <td style={tdStyle}>{r.nature || '—'}</td>
                      <td style={tdNumStyle}>{amount(r.value_cy)}</td>
                      <td style={tdNumStyle}>{amount(r.value_py)}</td>
                      <td style={tdNumStyle}>{amount(r.balance_cy)}</td>
                      <td style={tdStyle}>
                        {inAoc2
                          ? <span style={r.arms_length === false ? naPillStyle : aocPillStyle}>{r.arms_length === false ? 'Part 1 — not arm’s length' : 'Part 2 — arm’s length'}</span>
                          : <span style={as18PillStyle}>AS 18 only</span>}
                      </td>
                      <td style={tdStyle} onClick={e => e.stopPropagation()}>
                        <ActionMenu id={`rpt-${r.local_id}`} items={actions} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={tfootStyle} colSpan={2}>{shownTxns.length} transaction{shownTxns.length === 1 ? '' : 's'}</td>
                  <td style={{ ...tfootStyle, textAlign: 'right' }}>{formatStatementAmount(txnTotal)}</td>
                  <td style={tfootStyle} colSpan={4} />
                </tr>
              </tfoot>
            </table>
          )}
        </>
      )}

      {editingParty && (
        <PartyForm
          party={editingParty}
          isNew={!parties.some(p => p.local_id === editingParty.local_id)}
          parties={parties}
          onCancel={() => setEditingParty(null)}
          onSave={saveParty}
        />
      )}
      {editingTxn && (
        <TransactionForm
          txn={editingTxn.txn}
          isNew={editingTxn.isNew}
          parties={parties}
          byId={byId}
          onCancel={() => setEditingTxn(null)}
          onSave={saveTxn}
        />
      )}
    </div>
  );
};

// ============================================================================
// Add / Edit forms
// ============================================================================

const PartyForm: React.FC<{
  party: RelatedParty;
  isNew: boolean;
  parties: RelatedParty[];
  onCancel: () => void;
  onSave: (p: RelatedParty) => void;
}> = ({ party, isNew, parties, onCancel, onSave }) => {
  const [p, setP] = useState<RelatedParty>(party);
  const set = (patch: Partial<RelatedParty>) => setP(prev => ({ ...prev, ...patch }));
  const isPerson = isPersonCategory(p.category);
  const people = parties.filter(x => isPersonCategory(x.category) && x.name.trim() && x.local_id !== p.local_id);
  const byId = new Map([...parties, p].map(x => [x.local_id, x]));
  const particulars = describeParty(p, byId);
  const changeCategory = (category: RelatedPartyCategory) => {
    const toEntity = !isPersonCategory(category);
    set({ category, ...(toEntity && !p.entity_type ? { entity_type: 'COMPANY' as const } : {}) });
  };
  return (
    <FactsFormShell
      title={isNew ? 'Add related party' : `Edit — ${party.name || 'related party'}`}
      onCancel={onCancel}
      onSave={() => onSave({ ...p, name: p.name.trim() })}
      saveDisabled={!p.name.trim()}
      saveHint={!p.name.trim() ? 'Enter the name.' : undefined}
    >
      <div style={factsFormGridStyle}>
        <FactsField label="Name" htmlFor="rp-name">
          <input id="rp-name" className="form-input" value={p.name} autoFocus onChange={e => set({ name: e.target.value })} />
        </FactsField>
        <FactsField label="Relationship category" htmlFor="rp-cat">
          <select id="rp-cat" className="form-input" value={p.category} onChange={e => changeCategory(e.target.value as RelatedPartyCategory)}>
            {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FactsField>
        {p.category === 'KMP' && (
          <FactsField label="Designation" htmlFor="rp-des" hint="Printed as the particulars, e.g. Managing Director.">
            <input id="rp-des" className="form-input" value={p.designation ?? ''} onChange={e => set({ designation: e.target.value })} />
          </FactsField>
        )}
        {p.category === 'RELATIVE_OF_KMP' && (
          <>
            <FactsField label="Relation" htmlFor="rp-rel">
              <input id="rp-rel" className="form-input" placeholder="e.g. Spouse" value={p.relation ?? ''} onChange={e => set({ relation: e.target.value })} />
            </FactsField>
            <FactsField label="Relative of" htmlFor="rp-of">
              <select id="rp-of" className="form-input" value={p.relative_of ?? ''} onChange={e => set({ relative_of: e.target.value || null })}>
                <option value="">— choose the KMP —</option>
                {parties.filter(k => k.category === 'KMP' && k.local_id !== p.local_id)
                  .map(k => <option key={k.local_id} value={k.local_id}>{k.name || '(unnamed)'}</option>)}
              </select>
            </FactsField>
          </>
        )}
        {!isPerson && (
          <FactsField label="Type of entity" htmlFor="rp-type">
            <select id="rp-type" className="form-input" value={p.entity_type ?? 'OTHER'} onChange={e => set({ entity_type: e.target.value as RelatedParty['entity_type'] })}>
              {ENTITY_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FactsField>
        )}
      </div>

      {!isPerson && (
        <div style={{ marginTop: 14 }}>
          <div className="form-label">Related through</div>
          {(p.links ?? []).length === 0 && <div style={subtleStyle}>No KMP / relative linked yet.</div>}
          {(p.links ?? []).map((l, i) => (
            <div key={i} style={linkRowStyle}>
              <select className="form-input" aria-label="Person" value={l.party_id}
                onChange={e => set({ links: (p.links ?? []).map((x, j) => (j === i ? { ...x, party_id: e.target.value } : x)) })}>
                {people.map(k => <option key={k.local_id} value={k.local_id}>{k.name}</option>)}
              </select>
              <select className="form-input" aria-label="Role" value={l.role}
                onChange={e => set({ links: (p.links ?? []).map((x, j) => (j === i ? { ...x, role: e.target.value as RelatedPartyLinkRole } : x)) })}>
                {LINK_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button type="button" className="btn btn-secondary btn-sm" aria-label="Remove link"
                onClick={() => set({ links: (p.links ?? []).filter((_, j) => j !== i) })}>
                <IconSystem.trash size={13} />
              </button>
            </div>
          ))}
          {people.length > 0 ? (
            <button type="button" style={linkBtnStyle}
              onClick={() => set({
                links: [...(p.links ?? []), {
                  party_id: people[0].local_id,
                  role: p.entity_type === 'PROPRIETORSHIP' ? 'PROPRIETOR' : p.entity_type === 'LLP' || p.entity_type === 'FIRM' ? 'PARTNER' : 'DIRECTOR',
                }],
              })}>
              + link a KMP / relative
            </button>
          ) : (
            <div style={subtleStyle}>Add the KMP / relatives to the register first.</div>
          )}
        </div>
      )}

      <div style={{ ...factsFormGridStyle, marginTop: 14 }}>
        <FactsField label="Particulars (as printed)" span>
          <div style={previewStyle}>{particulars || <em style={{ color: 'var(--text-secondary)' }}>— none yet —</em>}</div>
        </FactsField>
        <FactsField label="Own wording for the particulars (optional)" htmlFor="rp-det" span
          hint="Replaces the generated particulars in the note.">
          <input id="rp-det" className="form-input" value={p.details ?? ''} onChange={e => set({ details: e.target.value })} />
        </FactsField>
      </div>
    </FactsFormShell>
  );
};

const TransactionForm: React.FC<{
  txn: RelatedPartyTransaction;
  isNew: boolean;
  parties: RelatedParty[];
  byId: Map<string, RelatedParty>;
  onCancel: () => void;
  onSave: (t: RelatedPartyTransaction) => void;
}> = ({ txn, isNew, parties, byId, onCancel, onSave }) => {
  const [t, setT] = useState<RelatedPartyTransaction>(txn);
  // A new transaction takes its nature's AOC-2 default until the auditor sets it.
  const [aocTouched, setAocTouched] = useState(!isNew || txn.in_aoc2 !== undefined);
  const set = (patch: Partial<RelatedPartyTransaction>) => setT(prev => ({ ...prev, ...patch }));
  const linked = t.party_id ? byId.get(t.party_id) : undefined;
  const inAoc2 = t.in_aoc2 !== false;
  const num = (v: string): number | null => {
    const s = v.trim().replace(/,/g, '');
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  const partyOk = !!(t.party_id || t.party.trim());
  return (
    <FactsFormShell
      title={isNew ? 'Add related-party transaction' : `Edit transaction — ${linked?.name ?? t.party ?? ''}`}
      onCancel={onCancel}
      onSave={() => onSave(t)}
      saveDisabled={!partyOk}
      saveHint={!partyOk ? 'Choose the related party.' : undefined}
    >
      <div style={factsFormGridStyle}>
        <FactsField label="Related party" htmlFor="rpt-party">
          {parties.length > 0 ? (
            <select id="rpt-party" className="form-input" value={t.party_id ?? ''}
              onChange={e => {
                const p = byId.get(e.target.value);
                set(e.target.value ? { party_id: e.target.value, party: p?.name ?? t.party } : { party_id: null });
              }}>
              <option value="">{t.party ? `${t.party} (not in the register)` : '— choose —'}</option>
              {parties.map(p => <option key={p.local_id} value={p.local_id}>{p.name || '(unnamed)'}</option>)}
            </select>
          ) : (
            <input id="rpt-party" className="form-input" value={t.party} onChange={e => set({ party: e.target.value })} />
          )}
        </FactsField>
        <FactsField label="Relationship" htmlFor="rpt-rel">
          {linked ? (
            <div style={previewStyle} title="From the register">{describeParty(linked, byId) || shortCategory(linked.category)}</div>
          ) : (
            <input id="rpt-rel" className="form-input" value={t.relationship ?? ''} onChange={e => set({ relationship: e.target.value })} />
          )}
        </FactsField>
        <FactsField label="Nature of transaction" htmlFor="rpt-nature" span>
          <input id="rpt-nature" className="form-input" list="rpt-nature-suggestions" value={t.nature ?? ''}
            onChange={e => set({ nature: e.target.value, ...(aocTouched ? {} : { in_aoc2: defaultInAoc2(e.target.value) }) })} />
          <datalist id="rpt-nature-suggestions">
            {NATURE_SUGGESTIONS.map(n => <option key={n} value={n} />)}
          </datalist>
        </FactsField>
        <FactsField label="Value — current year" htmlFor="rpt-cy">
          <input id="rpt-cy" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={t.value_cy ?? ''}
            onChange={e => set({ value_cy: num(e.target.value) })} />
        </FactsField>
        <FactsField label="Value — previous year" htmlFor="rpt-py">
          <input id="rpt-py" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={t.value_py ?? ''}
            onChange={e => set({ value_py: num(e.target.value) })} />
        </FactsField>
      </div>

      <div style={sectionHeadStyle}>Outstanding balance (AS 18)</div>
      <div style={factsFormGridStyle}>
        <FactsField label="Balance heading" htmlFor="rpt-bl" hint="e.g. Trade receivables — defaults to the nature." span>
          <input id="rpt-bl" className="form-input" value={t.balance_label ?? ''} onChange={e => set({ balance_label: e.target.value })} />
        </FactsField>
        <FactsField label="Balance at year end" htmlFor="rpt-bcy">
          <input id="rpt-bcy" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={t.balance_cy ?? ''}
            onChange={e => set({ balance_cy: num(e.target.value) })} />
        </FactsField>
        <FactsField label="Balance — previous year end" htmlFor="rpt-bpy">
          <input id="rpt-bpy" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={t.balance_py ?? ''}
            onChange={e => set({ balance_py: num(e.target.value) })} />
        </FactsField>
      </div>

      <div style={sectionHeadStyle}>Form AOC-2 (Annexure B of the Board's Report)</div>
      <label style={checkLabelStyle}>
        <input type="checkbox" checked={inAoc2} onChange={e => { setAocTouched(true); set({ in_aoc2: e.target.checked }); }} />
        A contract / arrangement under s.188(1) — print it in Form AOC-2
      </label>
      {inAoc2 && (
        <>
          <label style={checkLabelStyle}>
            <input type="checkbox" checked={t.arms_length !== false} onChange={e => set({ arms_length: e.target.checked })} />
            At arm's length (Part 2) — untick for Part 1
          </label>
          <div style={factsFormGridStyle}>
            <FactsField label="Duration" htmlFor="rpt-dur">
              <input id="rpt-dur" className="form-input" placeholder="e.g. FY 2025-26" value={t.duration ?? ''} onChange={e => set({ duration: e.target.value })} />
            </FactsField>
            <FactsField label="Date of approval by the Board" htmlFor="rpt-app">
              <input id="rpt-app" type="date" className="form-input" value={t.approval_date ?? ''} onChange={e => set({ approval_date: e.target.value })} />
            </FactsField>
            <FactsField label="Salient terms" htmlFor="rpt-terms" hint="The value is added from the amounts above." span>
              <input id="rpt-terms" className="form-input" value={t.terms ?? ''} onChange={e => set({ terms: e.target.value })} />
            </FactsField>
            <FactsField label="Advances paid" htmlFor="rpt-adv">
              <input id="rpt-adv" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={t.advances ?? ''}
                onChange={e => set({ advances: num(e.target.value) })} />
            </FactsField>
            {t.arms_length === false && (
              <>
                <FactsField label="Date of special resolution u/s 188(1)" htmlFor="rpt-sr">
                  <input id="rpt-sr" type="date" className="form-input" value={t.special_resolution_date ?? ''} onChange={e => set({ special_resolution_date: e.target.value })} />
                </FactsField>
                <FactsField label="Justification for entering into the transaction" htmlFor="rpt-just" span>
                  <textarea id="rpt-just" className="form-textarea" rows={2} value={t.justification ?? ''} onChange={e => set({ justification: e.target.value })} />
                </FactsField>
              </>
            )}
          </div>
        </>
      )}
    </FactsFormShell>
  );
};

// ───── styles ──────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)', borderRadius: 8, padding: '0.75rem 1rem 1rem', marginBottom: '1rem',
  display: 'flex', flexDirection: 'column', gap: 8,
};
const toolbarStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const segmentStyle: React.CSSProperties = {
  display: 'inline-flex', padding: 2, gap: 2, borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
};
const segBtn = (on: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer',
  fontSize: '0.82rem', fontWeight: 600,
  background: on ? 'var(--primary-color, #1d4ed8)' : 'transparent', color: on ? '#fff' : 'var(--text-secondary)',
});
const subtleStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary)' };
const emptyRowStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: 6 };
const warnStyle: React.CSSProperties = { fontSize: '0.75rem', color: '#92400e' };
const countPillStyle: React.CSSProperties = {
  fontSize: '0.68rem', fontWeight: 700, padding: '0 7px', borderRadius: 999, background: 'rgba(148,163,184,0.25)',
};
const importBoxStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', fontSize: '0.78rem',
  display: 'flex', flexDirection: 'column', gap: 6,
};
const linkBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', color: 'var(--primary-color, #2563eb)', cursor: 'pointer', fontSize: '0.78rem', padding: 0,
};
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', background: 'var(--bg-primary)', borderRadius: 8, overflow: 'hidden', fontSize: '0.82rem',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)',
};
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right' };
const rowStyle: React.CSSProperties = { cursor: 'pointer' };
const tdStyle: React.CSSProperties = { padding: '7px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)', verticalAlign: 'middle' };
const tdMutedStyle: React.CSSProperties = { ...tdStyle, color: 'var(--text-secondary)' };
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const tfootStyle: React.CSSProperties = { padding: '7px 10px', fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' };
const groupRowStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.3,
};
const pillBase: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' };
const aocPillStyle: React.CSSProperties = { ...pillBase, background: '#dbeafe', color: '#1e40af' };
const naPillStyle: React.CSSProperties = { ...pillBase, background: '#fef3c7', color: '#92400e' };
const as18PillStyle: React.CSSProperties = { ...pillBase, background: 'var(--bg-secondary)', color: 'var(--text-secondary)' };
const warnDotStyle: React.CSSProperties = {
  marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: 999,
  background: '#fef3c7', color: '#92400e', fontSize: '0.7rem', fontWeight: 800,
};
const sectionHeadStyle: React.CSSProperties = {
  marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)',
};
const checkLabelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-primary)' };
const linkRowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1.4fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: 6 };
const previewStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 6, background: 'var(--bg-secondary)', fontSize: '0.82rem', color: 'var(--text-primary)', minHeight: 34,
};

export default RelatedPartyRegisterEditor;
